import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { formatDate, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { MdOutlineEdit, MdClose, MdMerge } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import { Campaign } from '~/types/campaigns';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import {
    fetchCampaigns,
    fetchCampaignSearchFilters,
    fetchStates,
} from '~/services/campaign';
import { useQueries } from '@tanstack/react-query';
import { ReactSelectInput } from '~/common/form/Inputs';
import moment from 'moment';
import { Pagination } from '~/components/Pagination';

const columnHelper = createColumnHelper<Campaign>();

const columns = [
    columnHelper.accessor('full_name', {
        header: () => 'Client',
        cell: (props) => {
            return (
                <>
                    <Link
                        className='font-bold text-primary'
                        to={`/campaigns/${props.row.original.campaign_id}`}
                    >
                        {props.renderValue()}
                    </Link>
                </>
            );
        },
    }),
    columnHelper.accessor('ntaf_id', {
        header: () => 'NTAF ID',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor((row) => row.patient_info?.location ?? '', {
        id: 'location',
        header: () => 'State',
        cell: (info) => {
            const location = info.getValue();
            if (!location) {
                return '';
            }
            const [, value] = location.split(',');
            return value ? value.trim() : location;
        },
    }),
    columnHelper.accessor((row) => row.campaign_type?.name ?? '', {
        id: 'campaign_type_name',
        header: () => 'Medical Reason',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row.patient_info?.medical_issue ?? '', {
        id: 'medical_issue',
        header: () => 'Type',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('client_services_coordinator', {
        id: 'csc',
        header: () => 'CSC',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor(
        (props) =>
            props.funded_date
                ? formatDate(parseISO(props.funded_date), 'MM/dd/yy')
                : '',
        {
            id: 'funded_date',
            header: () => 'Opened Date',
            cell: (props) => props.renderValue(),
        }
    ),
];

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type FilterOption = {
    label: string;
    value: string;
};

type Filters = {
    campaign_type_id: FilterOption[];
    medical_reason: FilterOption[];
    state: FilterOption[];
};

const initialFilters = {
    campaign_type_id: [],
    medical_reason: [],
    state: [],
};

type DateRange = {
    start_date: string;
    end_date: string;
};

const NewCampaigns: React.FC = () => {
    const [pageSize] = useState(20);
    const [pages, setPages] = useState<number>(20);
    const [page, setPage] = useState<number>(1);
    const [sort, setSort] = useState<Sort | null>(Sort.DESC);
    const [filters, setFilters] = useState<Filters | null>(null);
    const [selectedFilters, setSelectedFilters] =
        useState<Filters>(initialFilters);
    const [sortBy, setSortBy] = useState<string | null>('funded_date');
    const [dateRange, setDateRange] = useState<DateRange>({
        start_date: moment().subtract(60, 'days').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    });
    const results = useQueries({
        queries: [
            {
                queryKey: [
                    'campaigns',
                    { pageSize, sort, sortBy },
                    selectedFilters,
                    dateRange,
                    page,
                ],
                queryFn: () =>
                    fetchCampaigns(
                        { pageSize, sort, sortBy, is_new: true },
                        selectedFilters,
                        dateRange,
                        page
                    ),
                staleTime: Infinity,
            },
            {
                queryKey: ['campaign_search_filters'],
                queryFn: () => fetchCampaignSearchFilters(),
                staleTime: Infinity,
            },
            {
                queryKey: ['states'],
                queryFn: () => fetchStates(),
                staleTime: Infinity,
            },
        ],
    });

    const campaignResults = results[0]?.data;

    useEffect(() => {
        const total = campaignResults?.count;
        setPages(Math.ceil(total / pageSize) || page);
    }, [campaignResults, pageSize]);

    useEffect(() => {
        const filterData = results[1]?.data;
        const statesData = results[2]?.data;
        if (filterData && statesData && !filters) {
            const newFilters = {
                medical_reason: filterData.medical_reason.map(
                    (reason: { name: string; id: number }) => ({
                        label: reason.name,
                        value: String(reason.id),
                    })
                ),
                state: statesData.map((state: { state: string; abbreviation: string }) => ({
                    label: state.state,
                    value: state.abbreviation,
                })),
                campaign_type_id: filterData.campaign_types.map((type) => ({
                    label: type.name,
                    value: type.id,
                })),
            };
            setFilters(newFilters);
        }
    }, [results]);

    const table = useReactTable({
        data: campaignResults?.results,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const isLoading = results.some((result) => result.isLoading);
    const error = results.find((result) => result.error);

    if (error) return <p>Error: {(error as any).message}</p>;

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            if (sort === Sort.ASC) {
                setSort(Sort.DESC);
            } else {
                setSort(Sort.ASC);
            }
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    const handleFilter = (filterType: string) => (value: string) => {
        setPage(1);
        setSelectedFilters({
            ...selectedFilters,
            [filterType]: value,
        });
    };
    const handleDateRange = (dateRangeType: string, value: string) => {
        setPage(1);
        setDateRange({ ...dateRange, [dateRangeType]: value });
    };

    return (
        <>
            <Breadcrumb pageName='Newly Opened Campaigns' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-center justify-start gap-2 overflow-x-auto px-8 pb-4'>
                    <div className='w-full min-w-[150px] max-w-[200px]'>
                        <ReactSelectInput
                            options={filters?.medical_reason || []}
                            value={selectedFilters.medical_reason as any}
                            onChange={handleFilter('medical_reason')}
                            placeholder='Medical Reason'
                        />
                    </div>
                    <div className='w-full min-w-[150px] max-w-[200px]'>
                        <ReactSelectInput
                            options={filters?.campaign_type_id || []}
                            value={selectedFilters.campaign_type_id as any}
                            onChange={handleFilter('campaign_type_id')}
                            placeholder='Type'
                        />
                    </div>
                    <div className='w-full min-w-[150px] max-w-[200px]'>
                        <ReactSelectInput
                            options={filters?.state || []}
                            value={selectedFilters.state as any}
                            onChange={handleFilter('state')}
                            placeholder='State'
                        />
                    </div>
                    <input
                        type='date'
                        name='start_date'
                        className='w-full max-w-[137px] rounded border border-[#E2E8F0] p-[6px]'
                        value={dateRange.start_date}
                        onChange={(event) =>
                            handleDateRange('start_date', event.target.value)
                        }
                    />
                    to
                    <input
                        type='date'
                        name='end_date'
                        className='w-full  max-w-[137px] rounded border border-[#E2E8F0]  p-[6px]'
                        value={dateRange.end_date}
                        onChange={(event) =>
                            handleDateRange('end_date', event.target.value)
                        }
                    />
                    <Link
                        to='#'
                        className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                    >
                        <FaPlus />
                        Add Campaign
                    </Link>
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(pageSize, (campaignResults?.count || 0) - (page - 1) * pageSize)} of {campaignResults?.count || 0} results</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdClose className='text-red' size={18} />
                            Reject
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const useChevron =
                                            header.column.id === sortBy;
                                        let chevron = null;
                                        if (useChevron) {
                                            if (sort === Sort.ASC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            } else if (sort === Sort.DESC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            }
                                        }

                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        onClick={() =>
                                                            handleSort(
                                                                header.column.id
                                                            )
                                                        }
                                                        className='cursor-pointer'
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column
                                                                      .columnDef
                                                                      .header,
                                                                  header.getContext()
                                                              )}
                                                    </span>
                                                    <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                        <span className='inline-block'>
                                                            {chevron}
                                                        </span>
                                                    </div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th
                                        style={{
                                            width: '70px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                          <td
                                              className='text-right'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                          >
                                              <CampaignActionDropdown
                                                  campaignId={
                                                      row?.original?.campaign_id
                                                  }
                                              />
                                          </td>
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} setPage={setPage} pages={pages} totalCount={campaignResults?.count} />
            </section>
        </>
    );
};

export default NewCampaigns;
